/**
 * Engraving Protection Script
 * Prevents removal of engraving fees and validates cart
 * 
 */

(function() {
  'use strict';

  const ENGRAVING_TITLES = ['CT Custom Engraving', 'CT Custom Engraving Back'];
  const ENGRAVING_PROPERTY_KEYS = [
    'Side-one-line-1',
    'Side-one-line-2',
    'Side-one-line-3',
    'Side-one-line-4',
    'Side-one-font',
    'Side-one-image',
    'Side-two-line-1',
    'Side-two-line-2',
    'Side-two-line-3',
    'Side-two-line-4',
    'Side-two-font',
    'Side-two-image'
  ];
  const originalFetch = window.fetch;

  // Get cart data
  async function getCart() {
    const response = await originalFetch.call(window, '/cart.js');
    return response.json();
  }

  // Show message to customer
  function showMessage(message, isError = true) {
    const existing = document.querySelector('.engraving-protection-msg');
    if (existing) existing.remove();

    const div = document.createElement('div');
    div.className = 'engraving-protection-msg';
    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: ${isError ? '#e74c3c' : '#27ae60'};
        color: white;
        padding: 15px 25px;
        border-radius: 8px;
        z-index: 99999;
        box-shadow: 0 4px 15px rgba(0,0,0,0.3);
        font-size: 14px;
        max-width: 90%;
        text-align: center;
      ">
        ${message}
        <span onclick="this.parentElement.parentElement.remove()" style="
          margin-left: 15px;
          cursor: pointer;
          font-size: 18px;
        ">×</span>
      </div>
    `;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 5000);
  }

  // Check if item is engraving product
  function isEngravingProduct(title) {
    return ENGRAVING_TITLES.some(t => title && title.includes(t));
  }

  function hasEngravingPropertyValues(props) {
    if (!props) return false;

    return ENGRAVING_PROPERTY_KEYS.some(key => {
      const value = props[key];
      console.log('checking value', value);
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });
  }

  function isMatchingParent(item, engravingFor) {
    if (!engravingFor) return false;
    return item.title === engravingFor || item.product_title === engravingFor;
  }

  // Parse request body
  function parseRequestBody(body) {
    if (!body) return {};
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch (e) {
        const params = new URLSearchParams(body);
        return Object.fromEntries(params);
      }
    }
    return body;
  }

  // Override fetch to intercept cart changes
  window.fetch = async function(url, options = {}) {
    const urlString = typeof url === 'string' ? url : url.toString();

    // Intercept cart change requests
    if (urlString.includes('/cart/change')) {
      const cart = await getCart();
      let requestData = parseRequestBody(options.body);

      // Check URL parameters too
      if (urlString.includes('?')) {
        const urlParams = new URLSearchParams(urlString.split('?')[1]);
        if (urlParams.get('quantity')) requestData.quantity = urlParams.get('quantity');
        if (urlParams.get('line')) requestData.line = urlParams.get('line');
        if (urlParams.get('id')) requestData.id = urlParams.get('id');
      }

      const quantity = parseInt(requestData.quantity);
      
      // If trying to remove an item (quantity = 0)
      if (quantity === 0) {
        let itemToRemove = null;
        
        // Find by line number
        if (requestData.line) {
          const lineIndex = parseInt(requestData.line) - 1;
          itemToRemove = cart.items[lineIndex];
        }
        // Find by id/key
        else if (requestData.id) {
          itemToRemove = cart.items.find(item => 
            item.key === requestData.id || 
            item.id.toString() === requestData.id.toString()
          );
        }

        if (itemToRemove) {
          // CASE 1: Trying to remove engraving product directly
          if (isEngravingProduct(itemToRemove.product_title)) {
            const uniqueId = itemToRemove.properties?.['Unique-ID'];
            const engravingFor = itemToRemove.properties?.For;

            // Check if parent product still exists
            if (uniqueId && engravingFor) {
              const parentExists = cart.items.some(item => 
                isMatchingParent(item, engravingFor) && 
                item.properties?.['Unique-ID'] === uniqueId &&
                !isEngravingProduct(item.product_title)
              );

              if (parentExists) {
                showMessage('Engraving cannot be removed separately. Remove the main product to remove engraving.');
                return new Response(JSON.stringify(cart), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' }
                });
              }
            }
          }
          
          // CASE 2: Removing main product - also remove its engravings
          else {
            const uniqueId = itemToRemove.properties?.['Unique-ID'];
            
            if (uniqueId) {
              // Find all engraving items with same Unique-ID
              const engravingItems = cart.items.filter(item => 
                isEngravingProduct(item.product_title) && 
                item.properties?.['Unique-ID'] === uniqueId
              );

              // Remove engraving items first
              for (const engItem of engravingItems) {
                await originalFetch.call(window, '/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ id: engItem.key, quantity: 0 })
                });
              }
            }
          }
        }
      }
    }

    // Proceed with original request
    return originalFetch.apply(window, arguments);
  };

  // Validate cart integrity
  async function validateCart() {
    const cart = await getCart();
    
    const productsByUniqueId = {};
    const engravingsByUniqueId = {};

    cart.items.forEach(item => {
      const uniqueId = item.properties?.['Unique-ID'] || '';

      if (isEngravingProduct(item.product_title)) {
        if (!engravingsByUniqueId[uniqueId]) engravingsByUniqueId[uniqueId] = [];
        engravingsByUniqueId[uniqueId].push(item);
      } else {
        const props = item.properties || {};
        const hasEngravingText = hasEngravingPropertyValues(props) || uniqueId;
        if (hasEngravingText) {
          productsByUniqueId[uniqueId] = item;
        }
      }
    });

    // Remove orphaned engraving items (engraving without parent product)
    for (const [uniqueId, engravings] of Object.entries(engravingsByUniqueId)) {
      if (!productsByUniqueId[uniqueId]) {
        for (const eng of engravings) {
          await originalFetch.call(window, '/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: eng.key, quantity: 0 })
          });
        }
      }
    }

    // Check for products with engraving text but no engraving fee
    let hasIssue = false;
    for (const [uniqueId, product] of Object.entries(productsByUniqueId)) {
      const props = product.properties || {};
      const hasEngravingText = hasEngravingPropertyValues(props) || (props['Unique-ID'] && props['Unique-ID'].toString().trim() !== '');

      if (hasEngravingText && (!uniqueId || !engravingsByUniqueId[uniqueId])) {
        hasIssue = true;
        // Remove the product since engraving fee is missing or invalid
        await originalFetch.call(window, '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: product.key, quantity: 0 })
        });
      }
    }

    if (hasIssue) {
      showMessage('An item was removed due to missing engraving fee. Please add it again.');
      setTimeout(() => location.reload(), 1500);
    }
  }

  // Block checkout if cart is invalid
  async function validateBeforeCheckout(e) {
    const cart = await getCart();
    
    const productsByUniqueId = {};
    const engravingsByUniqueId = {};

    cart.items.forEach(item => {
      const uniqueId = item.properties?.['Unique-ID'] || '';

      if (isEngravingProduct(item.product_title)) {
        if (!engravingsByUniqueId[uniqueId]) engravingsByUniqueId[uniqueId] = [];
        engravingsByUniqueId[uniqueId].push(item);
      } else {
        const props = item.properties || {};
        const hasEngravingText = hasEngravingPropertyValues(props) || uniqueId;
        if (hasEngravingText) {
          productsByUniqueId[uniqueId] = item;
        }
      }
    });

    // Check for missing fees
    for (const [uniqueId, product] of Object.entries(productsByUniqueId)) {
      if (!engravingsByUniqueId[uniqueId]) {
        e.preventDefault();
        e.stopPropagation();
        showMessage('Cart error: Engraving fee missing. Please re-add your engraved item.');
        
        // Remove problematic item
        await originalFetch.call(window, '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: product.key, quantity: 0 })
        });
        
        setTimeout(() => location.reload(), 1500);
        return false;
      }
    }
  }

  // Initialize
  function init() {
    // Validate cart on page load
    validateCart();

    // Intercept checkout
    document.addEventListener('submit', function(e) {
      if (e.target.action?.includes('/checkout') || 
          e.target.querySelector('[name="checkout"]')) {
        validateBeforeCheckout(e);
      }
    }, true);

    document.addEventListener('click', function(e) {
      const btn = e.target.closest('[name="checkout"], .cart-checkout-btn, a[href="/checkout"]');
      if (btn) {
        validateBeforeCheckout(e);
      }
    }, true);

    console.log('✅ Engraving protection active');
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();